import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { ResourceDataDto } from '../../../models/Pharse2/ResourceData';
import { defaultThresholds } from '../config/thresholds';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MiniChartProps {
  data: ResourceDataDto[];
  unit: string;
  status?: 'normal' | 'alert';
  alertThreshold?: number | null; // Alert threshold from LocationGroup
}

const MiniChart: React.FC<MiniChartProps> = ({ data, unit, status = 'normal', alertThreshold }) => {
  // Sort data by dateTime in ascending order
  const sortedData = [...data].sort((a, b) => {
    if (!a.dateTime || !b.dateTime) return 0;
    return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
  });

  // Get line color based on status
  const getLineColor = () => {
    if (status === 'alert') return 'rgb(239, 68, 68)'; // red-500
    return 'rgb(34, 197, 94)'; // green-500
  };

  // Get background color based on status
  const getBackgroundColor = () => {
    if (status === 'alert') return 'rgba(239, 68, 68, 0.1)';
    return 'rgba(34, 197, 94, 0.1)';
  };

  // 獲取 vibration 閾值配置（MiniChart 只用於 vibration）
  // 如果提供了 alertThreshold，使用動態閾值；否則使用默認閾值
  let max: number | undefined;

  if (alertThreshold !== null && alertThreshold !== undefined) {
    max = alertThreshold; // max = alertThreshold
  } else {
    const thresholdConfig = defaultThresholds.vibration;
    max = thresholdConfig.max;
  }

  const labels = sortedData.map(d => {
    if (!d.dateTime) return '';
    const date = new Date(d.dateTime);
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
  });

  const datasets: any[] = [
    {
      label: `Value (${unit})`,
      data: sortedData.map(d => d.value ?? 0),
      borderColor: getLineColor(),
      backgroundColor: getBackgroundColor(),
      tension: 0.4,
      pointRadius: 2,
      pointBackgroundColor: getLineColor(),
      pointBorderColor: '#fff',
      pointBorderWidth: 1,
      pointHoverRadius: 4,
      fill: true,
    },
  ];

  // 只添加 Alert 閾值線（移除 Warning 線）
  if (max !== undefined) {
    datasets.push({
      label: `Alert (${max} ${unit})`,
      data: labels.map(() => max),
      borderColor: 'rgb(239, 68, 68)', // red-500
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [5, 5], // 虛線
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: false,
      tension: 0,
    });
  }

  const chartData = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: getLineColor(),
        borderWidth: 1,
        padding: 8,
        callbacks: {
          title: function(context: any) {
            const index = context[0].dataIndex;
            const dataPoint = sortedData[index];
            if (dataPoint && dataPoint.dateTime) {
              const date = new Date(dataPoint.dateTime);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const seconds = String(date.getSeconds()).padStart(2, '0');
              return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
            }
            return context[0].label || '';
          },
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(3);
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 9,
          },
          maxRotation: 45,
          minRotation: 45,
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
          display: false,
        },
      },
      y: {
        display: true,
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          font: {
            size: 9,
          },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  };

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center text-white text-xs opacity-50">
        No chart data
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <Line data={chartData} options={options} />
    </div>
  );
};

export default MiniChart;

